import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
  FormGroup,
  FormControlLabel,
  Switch,
  List,
  DialogContent,
} from "@mui/material";
import {
  AUCTION_STATUS_OPTIONS_LIST,
  STATUS_OPTIONS_LIST,
} from "../../../utils/constants";
import React, { useEffect, useState, useCallback } from "react";
import TableFilter from "../../../components/shared/TableFilter";
import { AuctionServices } from "../../../services/AllServices";
import { Loader } from "../../../components/CustomLoader";
import DeleteIcon from "@mui/icons-material/Delete";
import errorHandler from "../../../utils/errorHandler";
import {
  AirplaneTicketSharp,
  EditOutlined,
  ListAlt,
  LiveTv,
} from "@mui/icons-material";
import DataTable from "react-data-table-component";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import ViewUserManagement from "./../ViewAuction";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import Swal from "sweetalert2";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { showDateTime } from "../../../utils/formatTime";
import { capitalize } from "../../../utils/helpers";

export default function AuctionList() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [auctionStatus, setAuctionStatus] = useState([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState([]);

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
      auctionStatus: auctionStatus,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await AuctionServices.getAuction(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.data);
          setTotalPages(res?.data?.data?.auctionList);
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchUsers(query), 400),
    [currentPage, pageSize, status, auctionStatus]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete this Auction?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await AuctionServices.deleteAuction({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setTotalPages((prevTotal) => prevTotal - 1);
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const res = await AuctionServices.changeStatus({
        id,
        status: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, status, refresh, auctionStatus]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "100px",
    },
    {
      name: "Category",
      sortable: true,
      selector: (row) => capitalize(row.categoryId?.[0]?.name || ""),
    },
    {
      name: "Ticket",
      sortable: true,
      cell: (row) => {
        const ticketNames =
          row.ticketData?.map((t) => t?.name).filter(Boolean) || [];
        const firstTwo = ticketNames.slice(0, 2).join(", ");
        const moreCount = ticketNames.length - 2;

        return (
          <>
            <span>{capitalize(firstTwo)}</span>
            {ticketNames.length > 1 && (
              <Button
                size="small"
                onClick={() => {
                  setSelectedTickets(ticketNames);
                  setShowTicketModal(true);
                }}
                style={{ marginLeft: 8 }}
              >
                +{moreCount} more
              </Button>
            )}
          </>
        );
      },
    },
    {
      name: "Auction Status",
      sortable: true,
      selector: (row) =>
        capitalize(
          row.auctionStatus === "upComming" ? "Upcoming" : row.auctionStatus
        ),
    },
    {
      name: "Start Date",
      sortable: true,
      selector: (row) => showDateTime(row.startDate),
    },
    {
      name: "End Date",
      sortable: true,
      selector: (row) => showDateTime(row.endDate),
    },
    {
      name: "Status",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === "active"}
                onClick={() => {
                  setShow(true);
                  setCurrentStatus(row.status);
                  setStatusId(row._id);
                  setWarning(
                    `Are you sure you want to ${
                      row.status === "active" ? "deactivate" : "activate"
                    } this Auction?`
                  );
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        </FormGroup>
      ),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                setModalShow(true);
                setViewId(row._id);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Live Bidding" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(
                  `/bidding-control/bidnow/${row?.ticket[0]}/${row?._id}`
                );
              }}
            >
              <LiveTv />
            </IconButton>
          </Tooltip>
          <Tooltip title="All Tickets" placement="left">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/bidding-control/edit/${row?._id}`);
              }}
            >
              <ListAlt />
            </IconButton>
          </Tooltip>
          {/* <Tooltip title="Delete" placement="top">
            <IconButton color="primary" onClick={() => deleteData(row._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip> */}
        </>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader title="Bidding Control" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <TableFilter
          data={AUCTION_STATUS_OPTIONS_LIST}
          value={auctionStatus}
          setValue={setAuctionStatus}
          filterName="Auction Status"
        />

        <TableFilter
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />

        <Input
          value={searchItem}
          placeholder="Search"
          onChange={handleSearchChange}
          sx={{ ml: 3, mr: 3 }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  const cleared = "";
                  setSearchItem(cleared);
                  debouncedSearch(cleared);
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
      </Box>
      <CardContent>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
              },
            },
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage) =>
            setPageSize(currentRowsPerPage)
          }
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          className="custom"
        />
      </CardContent>
      <Dialog
        open={show}
        onClose={() => setShow(false)}
        className="popupbox-statis"
      >
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShow(false)}
            color="primary"
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
      {modalShow ? (
        <ViewUserManagement
          show={modalShow}
          onHide={() => setModalShow(false)}
          id={viewId}
        />
      ) : (
        ""
      )}

      <Dialog open={showTicketModal} onClose={() => setShowTicketModal(false)}>
        <DialogTitle>All Tickets</DialogTitle>
        <DialogContent>
          <ul>
            {selectedTickets.map((name, index) => (
              <li key={index}>{capitalize(name)}</li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTicketModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
